import {memo, PropTypes, propTypesExact, shapeComponent, ShapeComponent, Text, useStyles, View} from "shared/base"
import {Platform, Pressable, StyleSheet} from "react-native"
import React, {useMemo} from "react"
import AppHistory from "shared/history"
import Icon from "@kaspernj/api-maker/build/utils/icon"
import Link from "@kaspernj/api-maker/build/link"
import mergeStyles from "@kaspernj/api-maker/build/merge-styles"

const styles = StyleSheet.create({
  text: {
    color: "#2c2c96",
    fontSize: 16,
    fontWeight: 600,
    textAlign: "center"
  },
  textDisabled: {
    color: "#fff"
  },
  textPurple: {
    color: "#fff"
  },
  textPrimary: {
    color: "#fff"
  },
  textSmall: {
    fontSize: 14
  }
})

/**
 * @typedef {object} ChalkyLayoutButtonProps
 * @property {unknown} dataSet
 * @property {boolean} disabled
 * @property {unknown} icon
 * @property {string} iconPosition
 * @property {unknown} onPress
 * @property {boolean} primary
 * @property {boolean} purple
 * @property {unknown} size
 * @property {boolean} small
 * @property {unknown} style
 * @property {unknown} testID
 * @property {unknown} textStyle
 * @property {unknown} title
 * @property {unknown} to
 */

/** @typedef {object} ChalkyLayoutButtonState */

/**
 * @extends {ShapeComponent<ChalkyLayoutButtonProps, ChalkyLayoutButtonState>}
 */
class ChalkyLayoutButton extends ShapeComponent {
  static defaultProps = {
    dataSet: null,
    disabled: false,
    icon: null,
    iconPosition: "right",
    onPress: null,
    primary: false,
    purple: false,
    size: null,
    small: false,
    style: null,
    textStyle: null,
    title: null,
    to: null
  }

  static propTypes = propTypesExact({
    dataSet: PropTypes.object,
    disabled: PropTypes.bool.isRequired,
    icon: PropTypes.string,
    iconPosition: PropTypes.string.isRequired,
    onPress: PropTypes.func,
    primary: PropTypes.bool.isRequired,
    purple: PropTypes.bool.isRequired,
    size: PropTypes.string,
    small: PropTypes.bool.isRequired,
    style: PropTypes.object,
    textStyle: PropTypes.object,
    testID: PropTypes.string,
    title: PropTypes.string,
    to: PropTypes.string
  })

  setup() {
    const {disabled, primary, purple, small} = this.p

    this.textStyle = useStyles(styles, ["text", {
      textDisabled: disabled,
      textPrimary: primary && !disabled,
      textPurple: purple && !disabled,
      textSmall: small
    }], [disabled, primary])
  }

  render() {
    const {dataSet, disabled, icon, primary, purple, size, small, style, to, title} = this.p

    const actualButtonStyle = useMemo(() => {
      let squared = false

      if (icon && !title) {
        squared = true
      }

      const defaultStyle = {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 16,
        paddingHorizontal: squared ? 16 : 46,
        backgroundColor: "#fff",
        borderColor: "#1b1f72",
        borderRadius: 22,
        borderWidth: 1
      }

      if (purple && !disabled) {
        defaultStyle.backgroundColor = "#5253ff"
        defaultStyle.borderColor = "#5253ff"
      }

      if (primary && !disabled) {
        defaultStyle.backgroundColor = "#5253ff"
        defaultStyle.borderColor = "#5253ff"
      }

      if (disabled) {
        defaultStyle.backgroundColor = "#b7b8ff"
        defaultStyle.borderColor = "#b7b8ff"
      }

      if (small) {
        defaultStyle.paddingVertical = 9
        defaultStyle.paddingHorizontal = squared ? 9 : 16
        defaultStyle.borderRadius = 8
      }

      if (size) {
        if (size == "extra-small") {
          defaultStyle.paddingVertical = 5
          defaultStyle.paddingHorizontal = squared ? 5 : 9
          defaultStyle.borderRadius = 8
        } else {
          throw new Error(`Unknown size: ${size}`)
        }
      }

      let actualStyle

      if (style) {
        actualStyle = mergeStyles([defaultStyle, style])
      } else {
        actualStyle = defaultStyle
      }

      return actualStyle
    }, [disabled, icon, primary, purple, small, size, style, title])

    const actualDataSet = useMemo(() =>
      Object.assign(
        {component: "chalky-layout/button", primary},
        dataSet
      )
    , [dataSet, primary])

    if (to && Platform.OS == "web") {
      return (
        <Link to={to}>
          <View dataSet={actualDataSet} onClick={this.tt.onLinkClick} style={actualButtonStyle} testID={this.props.testID}>
            {this.content()}
          </View>
        </Link>
      )
    } else {
      return (
        <Pressable dataSet={actualDataSet} onPress={this.tt.onPress} style={actualButtonStyle} testID={this.props.testID}>
          {this.content()}
        </Pressable>
      )
    }
  }

  content = () => {
    const {icon, iconPosition, size, textStyle, title} = this.p

    const actualTextStyle = useMemo(() => {
      const actualTextStyle = [...this.textStyle]

      if (textStyle) actualTextStyle.push(textStyle)

      if (size) {
        if (size == "extra-small") {
          actualTextStyle.push({
            fontSize: 10
          })
        } else {
          throw new Error(`Unknown size: ${size}`)
        }
      }

      return StyleSheet.flatten(actualTextStyle)
    }, [size, textStyle])

    let iconSize = 20
    let marginSize

    if (title) {
      marginSize = 10
    } else {
      marginSize = 0
    }

    if (size) {
      if (size == "extra-small") {
        iconSize = 10
      } else {
        throw new Error(`Unknown size: ${size}`)
      }
    }

    const iconColor = actualTextStyle.color

    return (
      <>
        {icon && iconPosition == "left" &&
          <View style={this.cache("iconLeftViewStyle", {marginRight: marginSize}, [marginSize])}>
            <Icon name={icon} size={iconSize} style={this.cache("iconLeftStyle", {color: iconColor}, [iconColor])} />
          </View>
        }
        {title &&
          <Text style={actualTextStyle}>
            {title}
          </Text>
        }
        {icon && iconPosition == "right" &&
          <View style={this.cache("iconRightViewStyle", {marginLeft: marginSize}, [marginSize])}>
            <Icon name={icon} size={iconSize} style={this.cache("iconRightStyle", {color: iconColor}, [iconColor])} />
          </View>
        }
      </>
    )
  }

  onLinkClick = (e, ...args) => {
    if (this.p.disabled) {
      e.preventDefault()
    } else if (this.p.onPress) {
      this.p.onPress(e, ...args)
    }
  }

  onPress = (...args) => {
    if (this.p.disabled) {
      return
    }

    if (this.p.onPress) {
      this.p.onPress(...args)
    }

    if (this.p.to) {
      AppHistory.push(this.p.to)
    }
  }
}

export default memo(shapeComponent(ChalkyLayoutButton))
